function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { reject, resolve } from 'rsvp';
import { getOwner } from '@ember/application';
import { assert } from '@ember/debug';
import { setProperties } from '@ember/object';
import Service from '@ember/service';
import { Attribute } from '@square/experiments/protos/squareup/feature/relay/common/attribute';
import { Token } from '@square/experiments/protos/squareup/feature/relay/common/token';
import { UserAttributes } from '@square/experiments/protos/squareup/feature/relay/common/user_attributes';
import { GetActivateExperimentRequest } from '@square/experiments/protos/squareup/feature/relay/experiments/message/get_activate_experiment';
import { GetExperimentRequest, GetExperimentsRequest } from '@square/experiments/protos/squareup/feature/relay/experiments/message/get_experiments';
import { Status } from '@square/experiments/protos/squareup/feature/relay/experiments/message/status';
import { TrackEventRequest } from '@square/experiments/protos/squareup/feature/relay/experiments/message/track_event';
import { ExperimentsService as ExperimentsServiceProto } from '@square/experiments/protos/squareup/feature/relay/experiments/service';
import rpc from '@square/experiments/utils/rpc';
import { getConsent, OneTrustConsent } from '@square/dashboard-utilities/utils/one-trust/one-trust-compliance';
const ONETRUST_CONSENT_LEVEL = OneTrustConsent.PERFORMANCE;
export default class SquareExperiments extends Service {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "baseAttributes", void 0);
    _defineProperty(this, "cdpKey", void 0);
    _defineProperty(this, "projectId", void 0);
  }
  get client() {
    return ExperimentsServiceProto.create(rpc((_method, guessedUrl) => guessedUrl));
  }
  initialize({
    projectId,
    baseAttributes,
    cdpKey
  }) {
    setProperties(this, {
      projectId,
      baseAttributes,
      cdpKey
    });
  }
  async getActivateExperiment({
    experimentName,
    token,
    additionalUserAttributes,
    cdpKey,
    projectId,
    tokenType = Token.Type.MERCHANT
  }) {
    // wait for onetrust consent to be provided
    const hasConsent = await getConsent(getOwner(this), ONETRUST_CONSENT_LEVEL);
    if (!hasConsent) {
      return reject(new Error('Experiment Relay: Performance tracking consent was rejected'));
    }
    const chosenProjectId = projectId || this.projectId;
    const chosenCdpKey = cdpKey || this.cdpKey;
    assert('A projectId must be defined', chosenProjectId);
    assert('A cdpKey must be defined', chosenCdpKey);
    const mergedUserAttributes = Object.assign({}, this.baseAttributes, additionalUserAttributes);
    try {
      const {
        status,
        variantName
      } = await this.client.getActivateExperiment(GetActivateExperimentRequest.create({
        cdpKey: chosenCdpKey,
        experimentName,
        projectId: chosenProjectId,
        userAttributes: Object.keys(mergedUserAttributes).length > 0 ? toUserAttributes(mergedUserAttributes) : undefined,
        userToken: Token.create({
          token,
          type: tokenType
        })
      }));
      if ([Status.SUCCESS, Status.IMPRESSION_LIMIT_EXCEEDED].includes(status)) {
        return resolve(variantName);
      } else {
        return reject(new Error(`getActivateExperiment failed. Status: ${status}`));
      }
    } catch (e) {
      return reject(e);
    }
  }
  async getExperiment({
    experimentName,
    token,
    additionalUserAttributes,
    projectId,
    tokenType = Token.Type.MERCHANT
  }) {
    // wait for onetrust consent to be provided
    const hasConsent = await getConsent(getOwner(this), ONETRUST_CONSENT_LEVEL);
    if (!hasConsent) {
      throw new Error('Experiment Relay: Performance tracking consent was rejected');
    }
    const chosenProjectId = projectId || this.projectId;
    assert('A projectId must be defined', chosenProjectId);
    const mergedUserAttributes = Object.assign({}, this.baseAttributes, additionalUserAttributes);
    const {
      experimentResponses
    } = await this.client.getExperiments(GetExperimentsRequest.create({
      experimentRequests: [GetExperimentRequest.create({
        experimentName,
        userToken: Token.create({
          token,
          type: tokenType
        })
      })],
      projectId: chosenProjectId,
      userAttributes: Object.keys(mergedUserAttributes).length > 0 ? toUserAttributes(mergedUserAttributes) : undefined
    }));
    if (experimentResponses?.length === 0) {
      throw new Error('getExperiment failed. No response');
    }
    return experimentResponses[0];
  }
  async trackEvent({
    eventName,
    token,
    additionalUserAttributes,
    cdpKey,
    eventAttributes,
    projectId,
    tokenType = Token.Type.MERCHANT
  }) {
    // wait for onetrust consent to be provided
    const hasConsent = await getConsent(getOwner(this), ONETRUST_CONSENT_LEVEL);
    if (!hasConsent) {
      return reject(new Error('Experiment Relay: Performance tracking consent was rejected'));
    }
    const chosenProjectId = projectId || this.projectId;
    const chosenCdpKey = cdpKey || this.cdpKey;
    assert('A projectId must be defined', chosenProjectId);
    assert('A cdpKey must be defined', chosenCdpKey);
    const mergedUserAttributes = Object.assign({}, this.baseAttributes, additionalUserAttributes);
    try {
      const trackEventResponse = await this.client.trackEvent(TrackEventRequest.create({
        cdpKey: chosenCdpKey,
        eventName,
        eventValues: eventAttributes ? toAttributesArray(eventAttributes) : undefined,
        projectId: chosenProjectId,
        userAttributes: Object.keys(mergedUserAttributes).length > 0 ? toUserAttributes(mergedUserAttributes) : undefined,
        userToken: Token.create({
          token,
          type: tokenType
        })
      }));
      if (trackEventResponse.status !== Status.SDK_ERROR) {
        return resolve(trackEventResponse);
      } else {
        return reject(new Error(`trackEvent failed. Status: ${status}`));
      }
    } catch (e) {
      return reject(e);
    }
  }
}
function toUserAttributes(additionalUserAttributes) {
  return UserAttributes.create({
    userAttributes: toAttributesArray(additionalUserAttributes)
  });
}
function toAttributesArray(additionalUserAttributes) {
  return Object.entries(additionalUserAttributes).map(([key, value]) => {
    if (typeof value === 'boolean') {
      return Attribute.create({
        name: key,
        boolValue: value
      });
    } else if (typeof value === 'number') {
      if (isInt(value)) {
        return Attribute.create({
          name: key,
          intValue: value
        });
      } else {
        return Attribute.create({
          name: key,
          doubleValue: value
        });
      }
    } else {
      return Attribute.create({
        name: key,
        stringValue: value
      });
    }
  });
}
function isInt(num) {
  return Math.trunc(num) === num;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.